import React, { useEffect, useState } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { CaretLeft, PencilSimple, UserPlus, QrCode, Download, Trash, X } from 'phosphor-react'
import { FlexRow, FlexRowBetween, LoaderBar } from 'components'
import { Header, Card, Divider, Modal, Button, Table } from 'semantic-ui-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { DesktopMode, MobileMode } from 'layout'
import styled from 'styled-components'
import {
    useAsset,
    useAssetCategory,
    useAssetLocation,
    useAssetInspection,
    useAssetActivity,
    useAssetInspectionItem,
    useAssetInspectionMedia,
} from 'hooks/asset-manager'
import {
    buildAssetQrPayload,
    getAssetQrImageUrl,
    printAssetLabel,
} from 'utility/asset-qr'
import { useAuth } from 'context/user'
import { getAssetPermissions } from 'utility/asset-permissions'
import { AssetMaintenanceTab } from 'page-components/maintenance'

const AssetDetailContainer = styled.div`
    padding: 20px;
    margin-top: 20px;
`

const InfoCard = styled(Card)`
    margin-bottom: 20px !important;
    border: 1px solid #e0e0e0 !important;
    border-radius: 4px !important;

    .header {
        font-weight: 600;
        font-size: 16px;
        margin-bottom: 16px;
        color: #333;
    }

    .content {
        padding: 16px !important;
    }

    .field-row {
        margin-bottom: 12px;
        display: flex;
        flex-direction: column;

        &:last-child {
            margin-bottom: 0;
        }

        .label {
            font-weight: 500;
            color: #666;
            margin-bottom: 4px;
            font-size: 14px;
        }

        .value {
            color: #333;
            font-size: 14px;
        }
    }
`

const NextInspectionRow = styled.div`
    display: flex;
    gap: 16px;
    margin-top: 20px;

    .inspection-card {
        flex: 1;
        border: 1px solid #e0e0e0;
        border-radius: 4px;
        padding: 16px;

        .header {
            font-weight: 600;
            font-size: 16px;
            margin-bottom: 12px;
            color: #333;
        }

        .date {
            color: #333;
            font-size: 14px;
        }
    }
`

const InspectionActionRow = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 12px;
    flex-wrap: wrap;
`

const MediaActionRow = styled.div`
    margin-top: 12px;

    .media-title {
        font-weight: 600;
        margin-bottom: 8px;
    }

    .media-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        margin-bottom: 8px;
        flex-wrap: wrap;
    }
`

const ActionsModalContent = styled.div`
    .action-item {
        padding: 12px 0;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 12px;
        color: #333;
        font-size: 14px;
        border-bottom: 1px solid #e8e9e9;

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            color: #2185d0;
        }

        &.danger {
            &:hover {
                color: #c62828;
            }
        }
    }
`

export const AssetDetailScreen = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [asset, setAsset] = useState(null)
    const [actionsModalOpen, setActionsModalOpen] = useState(false)
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
    const [deleteInspectionOpen, setDeleteInspectionOpen] = useState(false)
    const [deleteItemsOpen, setDeleteItemsOpen] = useState(false)
    const [selectedMediaToDelete, setSelectedMediaToDelete] = useState(null)
    const [activeTab, setActiveTab] = useState('details')
    const [activitySort, setActivitySort] = useState('newest')
    const assetFromState = location.state?.asset || null
    const { user } = useAuth()
    const permissions = getAssetPermissions(user)

    const {
        getAsset,
        loading: assetLoading,
        deleteAsset: deleteAssetAPI,
    } = useAsset()

    const { categories, fetchCategories } = useAssetCategory()
    const { locations, fetchLocations } = useAssetLocation()
    const {
        inspections,
        loading: inspectionLoading,
        fetchInspectionsByAsset,
        getInspection,
        deleteInspection,
    } = useAssetInspection()
    const {
        items,
        fetchInspectionItems,
        deleteByInspection,
    } = useAssetInspectionItem()
    const {
        media,
        fetchInspectionMedia,
        deleteInspectionMedia,
    } = useAssetInspectionMedia()
    const {
        activities,
        loading: activityLoading,
        fetchActivities,
        captureActivity,
    } = useAssetActivity()

    useEffect(() => {
        const fetchAssetData = async () => {
            if (!id) return
            if (assetFromState) {
                setAsset(assetFromState)
                if (assetFromState.AssetId) {
                    fetchInspectionsByAsset(assetFromState.AssetId)
                    fetchActivities(assetFromState.AssetId, activitySort)
                }
                return
            }

            const assetData = await getAsset(id)
            if (assetData) {
                setAsset(assetData)
                // Fetch inspections for this asset
                if (assetData.AssetId) {
                    fetchInspectionsByAsset(assetData.AssetId)
                    fetchActivities(assetData.AssetId, activitySort)
                }
                return
            }

            setAsset(null)
        }
        fetchAssetData()
    }, [id, assetFromState, getAsset, fetchInspectionsByAsset, fetchActivities, activitySort])

    useEffect(() => {
        fetchCategories()
        fetchLocations()
    }, [fetchCategories, fetchLocations])

    useEffect(() => {
        const syncInspectionRelations = async () => {
            const firstInspectionId =
                inspections?.[0]?.AssetInspectionId || inspections?.[0]?.Id
            if (!firstInspectionId) return
            const inspection = await getInspection(firstInspectionId)
            const effectiveInspectionId =
                inspection?.AssetInspectionId || inspection?.Id || firstInspectionId
            fetchInspectionItems(effectiveInspectionId)
            fetchInspectionMedia(effectiveInspectionId)
        }
        syncInspectionRelations()
    }, [
        inspections,
        getInspection,
        fetchInspectionItems,
        fetchInspectionMedia,
    ])

    const loading = assetLoading || inspectionLoading
    const categoriesForUi = Array.isArray(categories) ? categories : []
    const locationsForUi = Array.isArray(locations) ? locations : []
    const inspectionsForUi = Array.isArray(inspections) ? inspections : []
    const firstInspectionId =
        inspectionsForUi?.[0]?.AssetInspectionId || inspectionsForUi?.[0]?.Id

    const renderDesktopActivityContent = () => {
        if (activityLoading) {
            return <LoaderBar content="Loading activity..." />
        }
        if (activities.length === 0) {
            return (
                <div style={{ color: '#666' }}>
                    No activity captured yet.
                </div>
            )
        }
        return (
            <Table celled>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Date</Table.HeaderCell>
                        <Table.HeaderCell>User</Table.HeaderCell>
                        <Table.HeaderCell>Action</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {activities.map(item => (
                        <Table.Row key={item.id}>
                            <Table.Cell>
                                {new Date(item.createdOn).toLocaleString()}
                            </Table.Cell>
                            <Table.Cell>{item.userName || '-'}</Table.Cell>
                            <Table.Cell>{item.action || '-'}</Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>
        )
    }

    const renderMobileActivityContent = () => {
        if (activityLoading) {
            return <LoaderBar content="Loading..." />
        }
        if (activities.length === 0) {
            return <div style={{ color: '#666' }}>No activity captured yet.</div>
        }
        return (
            <div>
                {activities.map(item => (
                    <div
                        key={item.id}
                        style={{
                            padding: '8px 0',
                            borderBottom: '1px solid #e5e7eb',
                        }}
                    >
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                            {new Date(item.createdOn).toLocaleString()}
                        </div>
                        <div style={{ fontWeight: 600 }}>
                            {item.userName || '-'}
                        </div>
                        <div>{item.action || '-'}</div>
                    </div>
                ))}
            </div>
        )
    }

    const handleEditIconClick = () => {
        if (permissions.isViewOnly) return
        setActionsModalOpen(true)
    }

    const handleEdit = () => {
        if (!permissions.canEditAsset) return
        setActionsModalOpen(false)
        captureActivity({
            assetId: asset?.AssetId,
            action: 'Asset edit action opened',
        })
        navigate('/app/asset-management', { state: { editAsset: asset } })
    }

    const handleAssignInspection = () => {
        if (!permissions.canAssignInspection) return
        setActionsModalOpen(false)
        captureActivity({
            assetId: asset?.AssetId,
            action: 'Assign inspection clicked',
        })
        navigate('/app/asset-management/schedule-inspection', {
            state: { asset },
        })
    }

    const handleGenerateQR = () => {
        if (!permissions.canGenerateQr) return
        setActionsModalOpen(false)
        captureActivity({ assetId: asset?.AssetId, action: 'QR generated' })
        if (!asset) return
        const payload = buildAssetQrPayload(asset)
        const qrUrl = getAssetQrImageUrl(payload)
        const a = document.createElement('a')
        a.href = qrUrl
        a.download = `${asset.AssetTag || asset.assetId || 'asset'}-qr.png`
        a.click()
    }

    const handleExport = () => {
        if (!permissions.canPrintLabel) return
        setActionsModalOpen(false)
        captureActivity({ assetId: asset?.AssetId, action: 'Label printed' })
        if (!asset) return
        const payload = buildAssetQrPayload(asset)
        const qrUrl = getAssetQrImageUrl(payload)
        printAssetLabel({ asset, qrUrl })
    }

    const handleDelete = async () => {
        if (!permissions.canDeleteAsset) return
        setActionsModalOpen(false)
        setDeleteConfirmOpen(true)
    }

    const confirmDelete = async () => {
        const success = await deleteAssetAPI(asset.AssetId || id)
        if (success) {
            captureActivity({
                assetId: asset?.AssetId || id,
                action: 'Asset deleted',
            })
            setDeleteConfirmOpen(false)
            navigate('/app/asset-management')
        }
    }

    const refreshInspectionRelatedData = async () => {
        if (!asset?.AssetId) return
        await fetchInspectionsByAsset(asset.AssetId)
        if (firstInspectionId) {
            await fetchInspectionItems(firstInspectionId)
            await fetchInspectionMedia(firstInspectionId)
        }
    }

    const confirmDeleteInspection = async () => {
        if (!firstInspectionId) return
        const success = await deleteInspection(firstInspectionId)
        if (success) {
            captureActivity({
                assetId: asset?.AssetId || id,
                action: 'Inspection deleted',
            })
            setDeleteInspectionOpen(false)
            refreshInspectionRelatedData()
        }
    }

    const confirmDeleteItems = async () => {
        if (!firstInspectionId) return
        const success = await deleteByInspection(firstInspectionId)
        if (success) {
            captureActivity({
                assetId: asset?.AssetId || id,
                action: 'Inspection items cleared',
            })
            setDeleteItemsOpen(false)
            refreshInspectionRelatedData()
        }
    }

    const confirmDeleteMedia = async () => {
        if (!selectedMediaToDelete) return
        const mediaId =
            selectedMediaToDelete.AssetInspectionMediaId ||
            selectedMediaToDelete.Id
        if (!mediaId) return
        const success = await deleteInspectionMedia(mediaId)
        if (success) {
            captureActivity({
                assetId: asset?.AssetId || id,
                action: 'Inspection media deleted',
            })
            setSelectedMediaToDelete(null)
            if (firstInspectionId) {
                fetchInspectionMedia(firstInspectionId)
            }
        }
    }

    if (loading) {
        return <LoaderBar content="Loading asset details..." />
    }

    if (!asset) {
        return (
            <AssetDetailContainer>
                <DesktopMode>
                    <Header as="h4" block>
                        <FlexRowBetween>
                            <FlexRow
                                onClick={() => navigate('/app/asset-management')}
                                className="align-items-center pointer"
                            >
                                <CaretLeft size={24} />
                                <div className="ms-2">Back</div>
                            </FlexRow>
                        </FlexRowBetween>
                    </Header>
                </DesktopMode>
                <MobileMode>
                    <div className="mb-3">
                        <FlexRowBetween className="mb-3">
                            <FlexRow
                                onClick={() => navigate('/app/asset-management')}
                                className="align-items-center pointer"
                            >
                                <CaretLeft size={24} />
                            </FlexRow>
                            <div
                                onClick={() => navigate('/app/asset-management')}
                                style={{
                                    color: '#1976d2',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                }}
                            >
                                Cancel
                            </div>
                        </FlexRowBetween>
                        <h2 style={{ marginBottom: '8px', fontWeight: 600 }}>
                            Asset Details
                        </h2>
                    </div>
                </MobileMode>
                <div>Asset not found</div>
            </AssetDetailContainer>
        )
    }

    return (
        <AssetDetailContainer>
            <DesktopMode>
                <Header as="h4" block>
                    <FlexRowBetween>
                        <FlexRow className="align-items-center">
                            <FlexRow
                                onClick={() => navigate('/app/asset-management')}
                                className="align-items-center pointer me-3"
                            >
                                <CaretLeft size={24} />
                                <div className="ms-2">Back</div>
                            </FlexRow>
                            <div style={{ fontSize: '24px', fontWeight: 600 }}>
                                Asset Details
                            </div>
                        </FlexRow>
                        <AppPrimaryButton
                            type="button"
                            onClick={handleEditIconClick}
                            style={{ minWidth: 'auto' }}
                            disabled={permissions.isViewOnly}
                        >
                            <PencilSimple size={20} />
                        </AppPrimaryButton>
                    </FlexRowBetween>
                </Header>

                <div style={{ marginTop: '20px' }}>
                    <div className="mb-3 d-flex gap-2">
                        <Button
                            type="button"
                            primary={activeTab === 'details'}
                            basic={activeTab !== 'details'}
                            onClick={() => setActiveTab('details')}
                        >
                            Asset Information
                        </Button>
                        <Button
                            type="button"
                            primary={activeTab === 'activity'}
                            basic={activeTab !== 'activity'}
                            onClick={() => setActiveTab('activity')}
                        >
                            Activity
                        </Button>
                        <Button
                            type="button"
                            primary={activeTab === 'maintenance'}
                            basic={activeTab !== 'maintenance'}
                            onClick={() => setActiveTab('maintenance')}
                        >
                            Maintenance
                        </Button>
                    </div>
                    {activeTab === 'details' && (
                        <>
                    <InfoCard>
                        <Card.Content>
                            <div className="header">Asset Name</div>
                            <div className="field-row">
                                <div className="value" style={{ fontSize: '16px', fontWeight: 500 }}>
                                    {asset.AssetName || asset.assetName}
                                </div>
                                <div className="value" style={{ color: '#666', marginTop: '4px' }}>
                                    ID: {asset.AssetTag || asset.AssetId || asset.assetId}
                                </div>
                            </div>
                        </Card.Content>
                    </InfoCard>

                    <InfoCard>
                        <Card.Content>
                            <div className="header">Asset Information</div>
                            <div className="field-row">
                                <div className="label">Category</div>
                                <div className="value">
                                    {categoriesForUi.find(cat => cat.AssetCategoryId === asset.AssetCategoryId)?.CategoryName || '-'}
                                </div>
                            </div>
                            <div className="field-row">
                                <div className="label">Status</div>
                                <div className="value">{asset.IsActive ? 'Active' : 'Inactive'}</div>
                            </div>
                            <div className="field-row">
                                <div className="label">Location</div>
                                <div className="value">
                                    {locationsForUi.find(loc => loc.AssetLocationId === asset.AssetLocationId)?.LocationName || '-'}
                                </div>
                            </div>
                            {asset.PurchaseDate && (
                                <div className="field-row">
                                    <div className="label">Purchase Date</div>
                                    <div className="value">
                                        {new Date(asset.PurchaseDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        })}
                                    </div>
                                </div>
                            )}
                        </Card.Content>
                    </InfoCard>

                    {inspectionsForUi && inspectionsForUi.length > 0 && (
                        <NextInspectionRow>
                            <div className="inspection-card">
                                <div className="header">Next Inspection</div>
                                <div className="date">
                                    {inspectionsForUi[0]?.InspectionDate
                                        ? new Date(inspectionsForUi[0].InspectionDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        })
                                        : 'No upcoming inspections'}
                                </div>
                                <div className="date" style={{ marginTop: 8 }}>
                                    Items: {Array.isArray(items) ? items.length : 0}
                                    {' | '}
                                    Media: {Array.isArray(media) ? media.length : 0}
                                </div>
                                <InspectionActionRow>
                                    <AppSecondaryButton
                                        type="button"
                                        onClick={() => setDeleteInspectionOpen(true)}
                                        disabled={!firstInspectionId}
                                    >
                                        Delete Inspection
                                    </AppSecondaryButton>
                                    <AppSecondaryButton
                                        type="button"
                                        onClick={() => setDeleteItemsOpen(true)}
                                        disabled={!firstInspectionId}
                                    >
                                        Clear Items
                                    </AppSecondaryButton>
                                </InspectionActionRow>
                                {Array.isArray(media) && media.length > 0 && (
                                    <MediaActionRow>
                                        <div className="media-title">Media</div>
                                        {media.map(m => (
                                            <div
                                                className="media-row"
                                                key={m.AssetInspectionMediaId || m.Id}
                                            >
                                                <span>
                                                    {m.FileName || m.Caption || 'Attachment'}
                                                </span>
                                                <AppSecondaryButton
                                                    type="button"
                                                    onClick={() =>
                                                        setSelectedMediaToDelete(m)
                                                    }
                                                >
                                                    Delete Media
                                                </AppSecondaryButton>
                                            </div>
                                        ))}
                                    </MediaActionRow>
                                )}
                            </div>
                        </NextInspectionRow>
                    )}
                        </>
                    )}
                    {activeTab === 'maintenance' && (
                        <InfoCard fluid>
                            <Card.Content>
                                <AssetMaintenanceTab asset={asset} />
                            </Card.Content>
                        </InfoCard>
                    )}
                    {activeTab === 'activity' && (
                        <InfoCard>
                            <Card.Content>
                                <FlexRowBetween className="mb-2">
                                    <div className="header mb-0">
                                        Activity Log & Audit Trail
                                    </div>
                                    <select
                                        className="form-select"
                                        style={{ maxWidth: 180 }}
                                        value={activitySort}
                                        onChange={e => setActivitySort(e.target.value)}
                                    >
                                        <option value="newest">Newest First</option>
                                        <option value="oldest">Oldest First</option>
                                    </select>
                                </FlexRowBetween>
                                {renderDesktopActivityContent()}
                            </Card.Content>
                        </InfoCard>
                    )}
                </div>
            </DesktopMode>

            <MobileMode>
                <div className="mb-3">
                    <FlexRowBetween className="mb-3">
                        <FlexRow
                            onClick={() => navigate('/app/asset-management')}
                            className="align-items-center pointer"
                        >
                            <CaretLeft size={24} />
                        </FlexRow>
                        <div
                            onClick={handleEditIconClick}
                            style={{
                                color: '#1976d2',
                                fontSize: '14px',
                                cursor: 'pointer',
                                opacity: permissions.isViewOnly ? 0.45 : 1,
                            }}
                        >
                            <PencilSimple size={20} />
                        </div>
                    </FlexRowBetween>

                    <h2 style={{ marginBottom: '8px', fontWeight: 600 }}>
                        Asset Details
                    </h2>
                    <div className="mb-3 d-flex gap-2">
                        <Button
                            type="button"
                            primary={activeTab === 'details'}
                            basic={activeTab !== 'details'}
                            onClick={() => setActiveTab('details')}
                            size="small"
                        >
                            Info
                        </Button>
                        <Button
                            type="button"
                            primary={activeTab === 'activity'}
                            basic={activeTab !== 'activity'}
                            onClick={() => setActiveTab('activity')}
                            size="small"
                        >
                            Activity
                        </Button>
                        <Button
                            type="button"
                            primary={activeTab === 'maintenance'}
                            basic={activeTab !== 'maintenance'}
                            onClick={() => setActiveTab('maintenance')}
                            size="small"
                        >
                            Maintenance
                        </Button>
                    </div>

                    {activeTab === 'details' && (
                        <>
                    <InfoCard>
                        <Card.Content>
                            <div className="header">Asset Name</div>
                            <div className="field-row">
                                <div className="value" style={{ fontSize: '16px', fontWeight: 500 }}>
                                    {asset.AssetName || asset.assetName}
                                </div>
                                <div className="value" style={{ color: '#666', marginTop: '4px' }}>
                                    ID: {asset.AssetTag || asset.AssetId || asset.assetId}
                                </div>
                            </div>
                        </Card.Content>
                    </InfoCard>

                    <InfoCard>
                        <Card.Content>
                            <div className="header">Asset Information</div>
                            <div className="field-row">
                                <div className="label">Category</div>
                                <div className="value">
                                    {categoriesForUi.find(cat => cat.AssetCategoryId === asset.AssetCategoryId)?.CategoryName || '-'}
                                </div>
                            </div>
                            <div className="field-row">
                                <div className="label">Status</div>
                                <div className="value">{asset.IsActive ? 'Active' : 'Inactive'}</div>
                            </div>
                            <div className="field-row">
                                <div className="label">Location</div>
                                <div className="value">
                                    {locationsForUi.find(loc => loc.AssetLocationId === asset.AssetLocationId)?.LocationName || '-'}
                                </div>
                            </div>
                            {asset.PurchaseDate && (
                                <div className="field-row">
                                    <div className="label">Purchase Date</div>
                                    <div className="value">
                                        {new Date(asset.PurchaseDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        })}
                                    </div>
                                </div>
                            )}
                        </Card.Content>
                    </InfoCard>

                    {inspectionsForUi && inspectionsForUi.length > 0 && (
                        <NextInspectionRow>
                            <div className="inspection-card">
                                <div className="header">Next Inspection</div>
                                <div className="date">
                                    {inspectionsForUi[0]?.InspectionDate
                                        ? new Date(inspectionsForUi[0].InspectionDate).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit',
                                        })
                                        : 'No upcoming inspections'}
                                </div>
                                <div className="date" style={{ marginTop: 8 }}>
                                    Items: {Array.isArray(items) ? items.length : 0}
                                    {' | '}
                                    Media: {Array.isArray(media) ? media.length : 0}
                                </div>
                                <InspectionActionRow>
                                    <AppSecondaryButton
                                        type="button"
                                        onClick={() => setDeleteInspectionOpen(true)}
                                        disabled={!firstInspectionId}
                                    >
                                        Delete Inspection
                                    </AppSecondaryButton>
                                    <AppSecondaryButton
                                        type="button"
                                        onClick={() => setDeleteItemsOpen(true)}
                                        disabled={!firstInspectionId}
                                    >
                                        Clear Items
                                    </AppSecondaryButton>
                                </InspectionActionRow>
                                {Array.isArray(media) && media.length > 0 && (
                                    <MediaActionRow>
                                        <div className="media-title">Media</div>
                                        {media.map(m => (
                                            <div
                                                className="media-row"
                                                key={m.AssetInspectionMediaId || m.Id}
                                            >
                                                <span>
                                                    {m.FileName || m.Caption || 'Attachment'}
                                                </span>
                                                <AppSecondaryButton
                                                    type="button"
                                                    onClick={() =>
                                                        setSelectedMediaToDelete(m)
                                                    }
                                                >
                                                    Delete Media
                                                </AppSecondaryButton>
                                            </div>
                                        ))}
                                    </MediaActionRow>
                                )}
                            </div>
                        </NextInspectionRow>
                    )}
                        </>
                    )}
                    {activeTab === 'maintenance' && (
                        <InfoCard fluid>
                            <Card.Content>
                                <AssetMaintenanceTab asset={asset} compact />
                            </Card.Content>
                        </InfoCard>
                    )}
                    {activeTab === 'activity' && (
                        <InfoCard>
                            <Card.Content>
                                <FlexRowBetween className="mb-2">
                                    <div className="header mb-0">Activity Log</div>
                                    <select
                                        className="form-select"
                                        style={{ maxWidth: 160 }}
                                        value={activitySort}
                                        onChange={e => setActivitySort(e.target.value)}
                                    >
                                        <option value="newest">Newest</option>
                                        <option value="oldest">Oldest</option>
                                    </select>
                                </FlexRowBetween>
                                {renderMobileActivityContent()}
                            </Card.Content>
                        </InfoCard>
                    )}
                </div>
            </MobileMode>

            <Modal
                open={deleteInspectionOpen}
                onClose={() => setDeleteInspectionOpen(false)}
                size="tiny"
            >
                <Modal.Header>Delete Inspection</Modal.Header>
                <Modal.Content>
                    Are you sure you want to delete this inspection?
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setDeleteInspectionOpen(false)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton
                        type="button"
                        onClick={confirmDeleteInspection}
                    >
                        Delete Inspection
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
            <Modal
                open={deleteItemsOpen}
                onClose={() => setDeleteItemsOpen(false)}
                size="tiny"
            >
                <Modal.Header>Clear Inspection Items</Modal.Header>
                <Modal.Content>
                    Are you sure you want to delete all items for this inspection?
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setDeleteItemsOpen(false)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton type="button" onClick={confirmDeleteItems}>
                        Clear Items
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
            <Modal
                open={!!selectedMediaToDelete}
                onClose={() => setSelectedMediaToDelete(null)}
                size="tiny"
            >
                <Modal.Header>Delete Inspection Media</Modal.Header>
                <Modal.Content>
                    Are you sure you want to delete this media file?
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setSelectedMediaToDelete(null)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton type="button" onClick={confirmDeleteMedia}>
                        Delete Media
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
            <Modal
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                size="tiny"
            >
                <Modal.Header>Delete Asset</Modal.Header>
                <Modal.Content>
                    Are you sure you want to delete this asset?
                </Modal.Content>
                <Modal.Actions>
                    <AppSecondaryButton
                        type="button"
                        onClick={() => setDeleteConfirmOpen(false)}
                    >
                        Cancel
                    </AppSecondaryButton>
                    <AppPrimaryButton type="button" onClick={confirmDelete}>
                        Delete
                    </AppPrimaryButton>
                </Modal.Actions>
            </Modal>
            <Modal
                open={actionsModalOpen}
                onClose={() => setActionsModalOpen(false)}
                size="small"
            >
                <Modal.Header>
                    <FlexRowBetween>
                        <div style={{ fontWeight: 600, fontSize: '18px' }}>Actions</div>
                        <X
                            className="pointer"
                            size={24}
                            onClick={() => setActionsModalOpen(false)}
                        />
                    </FlexRowBetween>
                </Modal.Header>
                <Modal.Content>
                    <ActionsModalContent>
                        {permissions.canEditAsset && (
                            <div className="action-item" onClick={handleEdit}>
                                <PencilSimple size={20} />
                                <span>Edit Asset</span>
                            </div>
                        )}
                        {permissions.canAssignInspection && (
                            <div className="action-item" onClick={handleAssignInspection}>
                                <UserPlus size={20} />
                                <span>Assign Inspection</span>
                            </div>
                        )}
                        {permissions.canGenerateQr && (
                            <div className="action-item" onClick={handleGenerateQR}>
                                <QrCode size={20} />
                                <span>Generate QR</span>
                            </div>
                        )}
                        {permissions.canPrintLabel && (
                            <div className="action-item" onClick={handleExport}>
                                <Download size={20} />
                                <span>Export</span>
                            </div>
                        )}
                        {permissions.canDeleteAsset && (
                            <>
                                <Divider />
                                <div className="action-item danger" onClick={handleDelete}>
                                    <Trash size={20} />
                                    <span>Delete Asset</span>
                                </div>
                            </>
                        )}
                    </ActionsModalContent>
                </Modal.Content>
            </Modal>
        </AssetDetailContainer>
    )
}
